import React from 'react';

import { AlertColor } from '@mui/material';

import { track } from '@hbf/analytics';

import {
  AnalyticsEvent,
  AnalyticsPageCategory,
} from '@kamernet/core/Analytics/types';
import { getSearchAlertSetEventPayload } from '@kamernet/core/Analytics/utilities/getSearchAlertSetEventPayload';
import { KamernetError } from '@kamernet/core/ApiClient';
import { OpResponseCode } from '@kamernet/core/ApiClient/Kamernet';
import { AppServicesContext } from '@kamernet/core/AppServices';
import { AuthContext } from '@kamernet/core/Auth/AuthContext';
import { AuthAction, AuthSource } from '@kamernet/core/Auth/types';
import { useAuthenticationRouting } from '@kamernet/core/Auth/useAuthenticationRouting';
import { reportError } from '@kamernet/core/Errors';
import { useIntl } from '@kamernet/core/Intl';

import {
  isCountrySearch,
  mapListingsSearchAllFiltersToListingRequest,
} from '@kamernet/modules/Search/utilities';

import { ListingsSearchContext } from '../ListingsSearchContext';

export type SearchAlertNotification = {
  message: string;
  severity: AlertColor;
};

export const useSearchAlert = (): {
  isSavingAlert: boolean;
  saveAlert: () => void;
  searchAlertNotification: SearchAlertNotification | null;
  closeAlertSnackbar: () => void;
} => {
  const { T } = useIntl();
  const { isAuthenticated } = AuthContext.useContext();
  const { navigateToAuthentication } = useAuthenticationRouting();
  const { apiClient } = AppServicesContext.useContext();
  const { allFilters, totalListingsCount } = ListingsSearchContext.useContext();

  const [isSavingAlert, setIsSavingAlert] = React.useState(false);
  const [searchAlertNotification, setSearchAlertNotification] =
    React.useState<SearchAlertNotification | null>(null);

  const saveAlert = React.useCallback(() => {
    if (isSavingAlert) {
      return;
    }

    if (isCountrySearch(allFilters)) {
      return;
    }

    if (!isAuthenticated) {
      navigateToAuthentication({
        action: AuthAction.Signup,
        source: AuthSource.SearchAlert,
      });
      return;
    }

    track(AnalyticsEvent.ClickedCreateSearchAlert, {
      citysearch: allFilters.cityName,
      page: AnalyticsPageCategory.SearchResults,
    });

    setIsSavingAlert(true);

    apiClient.alert
      .createAlert({
        createAlertRequest: {
          setSearchVisibility: true,
          listingRequest:
            mapListingsSearchAllFiltersToListingRequest(allFilters),
        },
      })
      .then(response => {
        if (response.OpResponse.Code === OpResponseCode.OPERATION_SUCCESSFUL) {
          setSearchAlertNotification({
            message: T('toast.saved_search'),
            severity: 'success',
          });
          track(
            AnalyticsEvent.SearchAlertSet,
            getSearchAlertSetEventPayload(allFilters, totalListingsCount),
          );
        } else if (
          response.OpResponse.Code === OpResponseCode.SEARCH_ALERT_SAVED_ALREADY
        ) {
          setSearchAlertNotification({
            message: T('toast.already_saved_search'),
            severity: 'error',
          });
        }

        setIsSavingAlert(false);
      })
      .catch((error: unknown) => {
        setIsSavingAlert(false);

        setSearchAlertNotification({
          message: T('CommonMessages.general_failure'),
          severity: 'error',
        });

        if (!(error instanceof KamernetError)) {
          reportError(new Error('Failed to save search alert'), {
            extra: {
              metaData: { cause: error },
            },
          });
        }
      });
  }, [
    allFilters,
    isAuthenticated,
    isSavingAlert,
    navigateToAuthentication,
    apiClient.alert,
    T,
    totalListingsCount,
  ]);

  const closeAlertSnackbar = React.useCallback(() => {
    setSearchAlertNotification(null);
  }, []);

  return {
    isSavingAlert,
    saveAlert,
    searchAlertNotification,
    closeAlertSnackbar,
  };
};
